import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import PdfExportPage from '@components/PdfExport/PdfExportPage';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import {
  HealthSummaryPdfTemplate,
  useHealthSummaryPdfData,
} from '@views/HealthSummary/Pdf';

const messages = defineMessages({
  back: {
    id: 'healthSummaryPdf.back',
    defaultMessage: 'Back to Health Summary',
  },
  printOrSave: {
    id: 'healthSummaryPdf.printOrSave',
    defaultMessage: 'Print / Save PDF',
  },
  loading: {
    id: 'healthSummaryPdf.loading',
    defaultMessage: 'Loading health summary…',
  },
  errorState: {
    id: 'healthSummaryPdf.errorState',
    defaultMessage:
      'Unable to load health summary. Please go back and try again.',
  },
});

function sanitizePdfFilename(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, '').trim();
}

function HealthSummaryPrintPage() {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile, isLoadingAuth },
  } = useSession();

  const { pdfData, loading, hasLoadError } = useHealthSummaryPdfData();

  useEffect(() => {
    if (!isLoadingAuth && !activeProfile) {
      void navigate(ROUTES.LOGIN);
    }
  }, [activeProfile, isLoadingAuth]);

  if (isLoadingAuth || !activeProfile) {
    return null;
  }

  const documentTitle = pdfData?.name
    ? `${sanitizePdfFilename(pdfData.name)} - health summary`
    : 'health summary';

  return (
    <PdfExportPage
      backLabel={formatMessage(messages.back)}
      documentTitle={documentTitle}
      errorText={formatMessage(messages.errorState)}
      isLoading={loading}
      isReady={!loading && !hasLoadError && !!pdfData}
      loadingText={formatMessage(messages.loading)}
      onBack={() => void navigate(ROUTES.HEALTH_SUMMARY)}
      printLabel={formatMessage(messages.printOrSave)}
    >
      {pdfData && <HealthSummaryPdfTemplate data={pdfData} />}
    </PdfExportPage>
  );
}

export default withErrorBoundary(HealthSummaryPrintPage, {
  FallbackComponent: ErrorBoundaryFallback,
});
